import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import { api } from '../api/client';
import type { PlaceholderTarget } from '../upload/uploader';
import { useDebounced } from '../lib/useDebounced';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

interface Row { id: string; assetCode: string; title: string }

/** Searchable list of placeholders (by title or asset ID); picks the asset an upload fills. */
export function PlaceholderPicker({ value, onChange }: { value: PlaceholderTarget | null; onChange: (v: PlaceholderTarget | null) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const debounced = useDebounced(text, 200).trim();
  const ref = useRef<HTMLDivElement>(null);
  useDismissOnOutside(ref, open, () => setOpen(false));
  const list = useQuery({
    queryKey: ['placeholders', debounced],
    enabled: open,
    queryFn: () => api<{ items: Row[] }>(`/api/v1/assets?${new URLSearchParams({ status: 'placeholder', sort: 'newest', limit: '20', ...(debounced ? { q: debounced } : {}) })}`),
  });
  return (
    <div className="menu-anchor placeholder-picker" ref={ref}>
      <button type="button" className="btn ghost sm" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="placeholder-target">
        {value ? <>Into <span className="mono">{value.assetCode}</span> · {value.title}</> : 'New asset'} <ChevronDown size={12} />
      </button>
      {value && <button type="button" className="btn ghost icon sm" aria-label="Create a new asset instead" title="Create a new asset instead" onClick={() => onChange(null)}><X size={12} /></button>}
      {open && (
        <div className="popover columns-popover placeholder-popover" role="dialog" aria-label="Choose a placeholder">
          <div className="search" style={{ marginBottom: 6 }}>
            <Search size={13} />
            <input className="input" autoFocus placeholder="Title or asset ID…" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search placeholders" />
          </div>
          <ul>
            <li><button type="button" className="facet-option" onClick={() => { onChange(null); setOpen(false); }}>New asset</button></li>
            {(list.data?.items ?? []).map((p) => (
              <li key={p.id}>
                <button type="button" className="facet-option" onClick={() => { onChange({ id: p.id, assetCode: p.assetCode, title: p.title }); setOpen(false); }} data-testid="placeholder-option">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                  <span className="count mono">{p.assetCode}</span>
                </button>
              </li>
            ))}
          </ul>
          {list.isSuccess && !list.data.items.length && <p className="faint" style={{ margin: 0, fontSize: 12 }}>No placeholders{debounced ? ` match “${debounced}”` : ' yet'}.</p>}
        </div>
      )}
    </div>
  );
}
